
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import apiService from '@/service/api-service';
import { formatDate } from '@/utils/formatters';
import { deviceTypeDict } from '@/constants';
import { toast } from 'sonner';
import {useIsMobile} from "@/hooks/use-mobile.tsx";

export interface TseData {
  serial: string;
  manufacturer: string;
  bsiCertification: string;
  firmware: string;
  validUntil: string;
  created: string;
  deviceSerial: string;
  deviceType: string;
}

interface TseDetailsDialogProps {
  installationUuid: string;
  open: boolean;
  onClose: () => void;
}

const TseDetailsDialog = ({ installationUuid, open, onClose }: TseDetailsDialogProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [tseData, setTseData] = useState<TseData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && installationUuid) {
      fetchTseData();
    }
  }, [open, installationUuid]);

  const fetchTseData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get(
        `/installations/${installationUuid}/tse`
      );
      setTseData(response.data);
    } catch (err) {
      console.error('Failed to fetch TSE data:', err);
      setError(t('errors.failedToLoadTseData'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogContent sx={{ bgcolor: 'background.paper', p:4}}>
        {loading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" align="center" my={2}>
            {error}
          </Typography>
        )}

        {!loading && !error && tseData && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                {t('events.tseSecurityModule')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('tse.serial')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ wordBreak: 'break-all', flex: 1 }} gutterBottom>
                  {tseData.serial}
                </Typography>
                <Tooltip title={t('common.copyToClipboard')}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(tseData.serial);
                      toast.success(t('common.copiedToClipboard'));
                    }}
                    sx={{ color: 'primary.main' }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('tse.manufacturer')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {tseData.manufacturer}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('tse.bsiCertification')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {tseData.bsiCertification}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('tse.firmware')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {tseData.firmware}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('tse.validUntil')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formatDate(tseData.validUntil)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('tse.created')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formatDate(tseData.created)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ mt: 2 }} gutterBottom>
                {t('events.connectedDevice')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('events.deviceSerial')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {tseData.deviceSerial}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('events.deviceType')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {deviceTypeDict[tseData.deviceType]}
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'background.paper' }}>
        <Button onClick={onClose} color="primary">
          {t('events.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TseDetailsDialog;
